import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
// Remove AuthContext import
// import { useAuth } from '@/context/AuthContext';
// Remove useUsers import
// import { useUsers } from '@/hooks/firestore'; 
// Import RTK hooks, selectors, and thunks
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    selectUser,
    selectUserProfileDetails,
    selectAuthLoading,
    selectAuthLoaded,
    selectAuthError,
    checkUsernameExists,
    saveInitialUserProfile,
    logoutUser // Import logoutUser thunk for error handling
} from "@/store/slices/authSlice";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Keeping component name specific to its current function
const CreateUsernamePage = () => {
    const dispatch = useAppDispatch();
    // Get auth state from RTK store
    const currentUser = useAppSelector(selectUser);
    const userProfileDetails = useAppSelector(selectUserProfileDetails);
    const hasUsername = !!userProfileDetails?.username;
    const authLoading = useAppSelector(selectAuthLoading);
    const authLoaded = useAppSelector(selectAuthLoaded);
    const authError = useAppSelector(selectAuthError);

    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [error, setError] = useState<string | null>(null);
    // Local loading state for async checks/saves
    const [isChecking, setIsChecking] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // Use authLoading directly for initial load check
    const isLoading = authLoading === 'initializing' || authLoading === 'pending';

    useEffect(() => {
        if (authLoaded) {
            if (!currentUser) {
                navigate('/');
            } else if (hasUsername) {
                navigate('/');
            }
        }
    }, [currentUser, hasUsername, authLoaded, navigate]);

    // Display auth error from slice if it exists
    useEffect(() => {
        if (authError) {
            setError(authError);
        }
    }, [authError]);

    const validateUsername = (name: string): boolean => {
        const isValid = /^[a-zA-Z0-9_-]{3,16}$/.test(name);
        if (!isValid) {
            setError('Username must be 3-16 characters long and contain only letters, numbers, underscores, or hyphens.');
        } else {
            setError(null); // Clear previous validation error
        }
        return isValid;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!currentUser || !currentUser.id) {
            setError('You must be logged in to create a username.');
            return;
        }

        const trimmedUsername = username.trim();
        if (!validateUsername(trimmedUsername)) {
            return;
        }

        setIsChecking(true);
        try {
            const availabilityResult = await dispatch(checkUsernameExists(trimmedUsername));
            
            if (checkUsernameExists.rejected.match(availabilityResult)) {
                console.error("Username check failed:", availabilityResult.payload);
                const errorMessage = availabilityResult.payload;
                if (typeof errorMessage === 'string' && errorMessage.includes('permission-denied')) {
                    setError('Unable to verify username availability. Please try again or contact support.');
                } else {
                    setError(errorMessage || 'Failed to check username availability.');
                }
                return;
            }
            
            // checkUsernameExists returns true if username exists, so isAvailable is the opposite
            const isUsernameExists = availabilityResult.payload;
            if (isUsernameExists) {
                setError('This username is already taken. Please choose another one.');
                return;
            }
            
            setIsSaving(true);
            try {
                await dispatch(saveInitialUserProfile({
                    userId: currentUser.id,
                    username: trimmedUsername,
                    email: currentUser.email // Pass email if available
                })).unwrap(); // unwrap to catch potential rejection

                navigate('/');
            } catch (err: any) {
                // Catch unexpected errors during dispatch
                console.error("Error creating user profile:", err);
                setError(`Failed to create profile: ${err.message || err}`);
            }
        } catch (err: any) {
            console.error("Error during username check:", err);
            setError(`Error checking username: ${err.message || 'Unknown error'}`);
        } finally {
            setIsChecking(false);
            setIsSaving(false);
        }
    };

    const handleLogout = async () => {
        setIsSaving(true);
        try {
            await dispatch(logoutUser());
            navigate('/');
        } catch (err) {
            console.error("Error logging out:", err);
            setError('Failed to log out. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };
    
    const combinedLoading = isLoading || isChecking || isSaving;

    // Show loading spinner during initial auth check or redirection conditions
    if (isLoading || (authLoaded && (!currentUser || hasUsername))) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
                <span className="ml-3 text-foreground">Loading...</span>
            </div>
        );
    }

    // Only render the form if the user is logged in and does NOT have a username
    return (
        <div className="flex items-center justify-center h-screen">
            <Card className="w-full max-w-sm">
                <CardHeader>
                    <CardTitle className="text-2xl">Create Username</CardTitle>
                    <CardDescription>
                        Choose a unique username for your account.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => {
                                    setUsername(e.target.value);
                                    if (error) setError(null); 
                                }}
                                placeholder="Your unique username"
                                required
                                disabled={combinedLoading} // Use combined loading state
                                aria-describedby={error ? "username-error" : undefined}
                            />
                        </div>
                        {error && <p id="username-error" className="text-sm text-red-600">{error}</p>}
                        <Button type="submit" className="w-full" disabled={combinedLoading}> 
                            {isChecking ? 'Checking...' : isSaving ? 'Saving...' : 'Save Username'}
                        </Button>
                        
                        <div className="text-center pt-4">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={handleLogout} 
                                disabled={combinedLoading}
                                className="text-sm text-muted-foreground"
                            >
                                Cancel and Log Out
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

// Export using the specific name
export default CreateUsernamePage;
